import { createSharedComposable, useQuery, useQueryCache, useRequestEvent, useRequestFetch, useCookie, useDayjs, useUserInfo, useRoute, watch, computed, toValue, useIsKnownCrawler } from '#imports'
import type { MaybeRefOrGetter } from '#imports'
import { filterGlobalNotifications } from '../utils/filterGlobalNotifications'
import type { GlobalNotificationPlacement, GlobalNotificationsResponse } from '../types/globalNotifications'

const QUERY_KEY = 'message:global-notifications'

export const useGlobalNotifications = createSharedComposable(() => {
    const route = useRoute()
    const { isStaff } = useUserInfo()
    const dayjs = useDayjs()
    const isCrawler = useIsKnownCrawler()
    const dismissedIds = useCookie<number[]>('global-notifications-dismissed', {
        sameSite: 'lax',
        maxAge: dayjs.duration({ hours: 24 }).asSeconds(),
        path: '/',
        secure: true,
        httpOnly: false,
        default: isCrawler.value ? undefined : (): number[] => [],
    })

    // Handler form in the browser: the URL form would resolve against the public origin via useApi.
    const event = useRequestEvent()
    const requestFetch = useRequestFetch()
    const query = useQuery<GlobalNotificationsResponse>(
        context => event
            ? event.context.globalNotifications()
            : requestFetch<GlobalNotificationsResponse>('/api/public/global-notifications', { signal: context?.signal }),
        {
            key: QUERY_KEY,
            server: true,
        },
    )

    const queryCache = useQueryCache<GlobalNotificationsResponse>()

    // The list is path-independent and refresh() bypasses the cache, so navigation is only a
    // reason to fetch once the TTL has passed.
    watch(() => route.path, () => {
        if (queryCache.get(QUERY_KEY) !== undefined) return

        query.refresh()
    })

    function dismissedList(): number[] {
        return dismissedIds.value ?? []
    }

    function dismiss(id: number) {
        const current = dismissedList()
        if (!current.includes(id)) dismissedIds.value = [...current, id]
    }

    function forPlacement(placement: MaybeRefOrGetter<GlobalNotificationPlacement>) {
        return computed(() => filterGlobalNotifications(query.data.value?.notifications ?? [], {
            placement: toValue(placement),
            path: route.path,
            isStaff: isStaff.value,
            dismissedIds: new Set(dismissedList()),
        }))
    }

    return query.enrich({ forPlacement, dismiss, dismissedIds })
})
